import type { FluxErrorEntityErrorCodes } from '../types.js';

export const UserErrorCodes = {
  UserForbiddenProvider: {
    code: 'user/forbidden-provider',
    explanation:
      'O provedor de autenticação que você está usando não é suportado para este usuário.',
    localizations: {},
    message: 'O seu método de autenticação não é suportado por esta ação.',
  },
  UserForbiddenType: {
    code: 'user/forbidden-type',
    explanation:
      'O tipo de usuário que você está tentando acessar não é suportado para esta ação, por exemplo, não é possível adicionar um usuário do tipo "Time" em um time.',
    localizations: {},
    message: 'O tipo de usuário não é suportado para esta ação.',
  },
  UserInvalidIdentifiable: {
    code: 'user/invalid-identifiable',
    explanation:
      'O identificador fornecido para o usuário é inválido. O identificador deve conter um e-mail ou ID válido, caso os dois sejam fornecidos, o ID terá prioridade.',
    localizations: {},
    message: 'O identificador fornecido para o usuário é inválido.',
  },
  UserNotFound: {
    code: 'user/not-found',
    explanation:
      'O usuário que você está tentando acessar não foi encontrado. Verifique se o e-mail ou ID está correto ou se o usuário ainda faz parte da conta. Você também pode tentar convidar o usuário novamente',
    localizations: {},
    message: 'Usuário não encontrado.',
  },
} as const satisfies FluxErrorEntityErrorCodes<'user'>;

export const UserInviteErrorCodes = {
  UserInviteAboveRoleHierarchy: {
    code: 'user-invite/above-role-hierarchy',
    explanation:
      'Você não pode convidar um usuário para um papel (role) que está acima de você na hierarquia. Apenas usuários com cargos superiores na hierarquia podem fazer convites para este nível de permissão.',
    localizations: {},
    message: 'Você não tem permissão para convidar usuários para este cargo.',
  },
  UserInviteAlreadyInteracted: {
    code: 'user-invite/already-interacted',
    explanation: 'O convite de usuário que você está tentando acessar já foi aceito ou recusado.',
    localizations: {},
    message: 'O convite já foi aceito ou recusado.',
  },
  UserInviteInvalidToken: {
    code: 'user-invite/invalid-token',
    explanation: 'O token do convite de usuário é inválido ou expirou.',
    localizations: {},
    message: 'O token do convite é inválido ou expirou.',
  },
  UserInviteNotFound: {
    code: 'user-invite/not-found',
    explanation: 'O convite de usuário que você está tentando acessar não foi encontrado.',
    localizations: {},
    message: 'Convite não encontrado.',
  },
  UserInviteRoleNotFound: {
    code: 'user-invite/role-not-found',
    explanation: 'O papel (role) especificado no convite de usuário não foi encontrado.',
    localizations: {},
    message: 'O cargo especificado no convite não foi encontrado.',
  },
} as const satisfies FluxErrorEntityErrorCodes<'user-invite'>;

export const UserLicenseErrorCodes = {
  UserLicenseAlreadyExists: {
    code: 'user-license/already-exists',
    explanation:
      'O usuário já possui uma licença ativa. Cada usuário só pode ter uma licença ativa por vez.',
    localizations: {},
    message: 'Este usuário já possui uma licença ativa.',
    supportingDocs: [
      {
        name: 'Licenças',
        path: '/wiki/docs/licencas/intro',
      },
      {
        name: 'Alterando licenças',
        path: '/wiki/docs/licencas/alterando-licencas',
      },
    ],
  },
  UserLicenseGlobalOwnerForbidden: {
    code: 'user-license/global-owner-forbidden',
    explanation: 'Você não pode alterar a licença do proprietário da conta global.',
    localizations: {},
    message: 'Não é possível alterar a licença do proprietário da conta.',
    supportingDocs: [
      {
        name: 'Licenças',
        path: '/wiki/docs/licencas/intro',
      },
      {
        name: 'Alterando licenças',
        path: '/wiki/docs/licencas/alterando-licencas',
      },
    ],
  },
  UserLicenseLimitReached: {
    code: 'user-license/limit-reached',
    explanation:
      'O número máximo de licenças ativas para esta conta foi atingido. Para aumentar o número de licenças, remova uma licença existente ou entre em contato com o suporte.',
    localizations: {},
    message:
      'O número máximo de licenças ativas para esta conta foi atingido. Remova uma licença existente para adicionar uma nova.',
    supportingDocs: [
      {
        name: 'Licenças',
        path: '/wiki/docs/licencas/intro',
      },
    ],
  },
  UserLicenseNotAssigned: {
    code: 'user-license/not-assigned',
    explanation:
      'O usuário não possui uma licença atribuída. Atribua uma licença para que ele possa acessar todos os recursos da plataforma.',
    localizations: {},
    message: 'Este usuário não possui uma licença atribuída.',
    supportingDocs: [
      {
        name: 'Licenças',
        path: '/wiki/docs/licencas/intro',
      },
      {
        name: 'Alterando licenças',
        path: '/wiki/docs/licencas/alterando-licencas',
      },
    ],
  },
} as const satisfies FluxErrorEntityErrorCodes<'user-license'>;
